import PhoneInput from "../../components/PhoneInput";
import Spinner from "../../components/Spinner";
import { ChangeEvent, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { createEligiblesApi, createPatientApi } from "../../api-helpers/PatientApiHelper";
import { ToastConfig } from "../../components/Toaster";
import {
  Contracts,
  AddEligiblePatient as EligiblePatient,
} from "patients-experience-client";
import { Datepicker, MbscCalendarEvent } from "@mobiscroll/react";
import { formatDateTask } from "../../helper/dateFormatter";
import { IdValuePairDTO, Lookup } from "common-util-experience-client";
import {
  createCommonUtilsApi,
  createLocationApi,
} from "../../api-helpers/CommonUtilsApiHelper";
import { Genders as genders } from "../../constants/Genders";
import { Section, SectionNavLayout } from "../../components/FormSections";

interface AddEligiblePatientProps {
  setOffCanvasShow: (data: boolean) => void;
  showToaster: (toasterConfig: ToastConfig) => void;
  reloadGrid?: () => void;
}

export const AddEligiblePatient = ({
  setOffCanvasShow,
  showToaster,
  reloadGrid,
}: AddEligiblePatientProps) => {
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [form, setForm] = useState({} as EligiblePatient);
  const [contracts, setContracts] = useState<Contracts[]>([]);
  const [selectedState, setSelectedState] = useState<string>("");
  const [cities, setCities] = useState<IdValuePairDTO[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [invalidZipCode, setInvalidZipCode] = useState<boolean>(false);
  const [openPicker, setOpenPicker] = useState<boolean>(false);
  const [error, setError] = useState({
    dateOfBirth: false,
  });

  const eligiblesApi = createEligiblesApi();
  const commonUtilApi = createCommonUtilsApi();
  const locationApi = createLocationApi();
  const patientApi = createPatientApi();

  const calculateMinDateFor18Years = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today;
  };
  const maxDate18YearsAgo = calculateMinDateFor18Years();

  const getContracts = async () => {
    try{
      const contracts = await patientApi.patientsControllerGetAllContracts();
      setContracts(contracts.data);
    }catch(error){
      console.log('Error loading Contracts')
    }
  };

  const fetchZipcodeDetails = async (zipcode: string) => {
    try {
      setLoading(true);
      setInvalidZipCode(false);
      const response = await locationApi.locationControllerGetZipcodeDetails(
        zipcode
      );
      // Assuming the API returns city, state, and other details
      const { state, cities } = response.data;
      setSelectedState(state.value);
      setForm((prevForm) => ({
        ...prevForm,
        stateId: state.id,
      }));
      if (cities.length === 1) {
        setSelectedCity(cities[0].value);
        setCities([]);
        setForm((prevForm) => ({
          ...prevForm,
          cityId: cities[0].id,
        }));
      } else if (cities.length > 1) {
        setCities(cities);
        setForm((prevForm) => ({
          ...prevForm,
          cityId: "",
        }));
      } else {
        // Handle case where no cities are returned
        console.error("No cities found for this zipcode");
      }
    } catch (error) {
      console.error(error);
      setCities([]);
      setInvalidZipCode(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const phoneInput = document.querySelector('[name="phone"]') as HTMLInputElement;
    const emailInput = document.querySelector('[name="email"]') as HTMLInputElement;
    phoneInput.setCustomValidity("");
    emailInput.setCustomValidity("");

    event.preventDefault();
    setValidated(true);

    if (!form.dateOfBirth) {
      setError({
        ...error,
        dateOfBirth: true,
      });
      return;
    }

    // check for valid date of birth
    if (form.dateOfBirth && form.dateOfBirth.getFullYear() < 1900) {
      showToaster({
        type: "danger",
        title: "Please enter valid date of birth.",
        icon: "error_outline",
      });
      setError({
        ...error,
        dateOfBirth: true,
      });
      return;
    }

    // Validate phone number
    if (form.phone && form?.phone?.length !== 10) {
      phoneInput.setCustomValidity("Invalid Phone Number");
      return;
    }

    if (form.email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailInput.value)) {
      emailInput.setCustomValidity("Invalid Email");
      return;
    }

    const formTarget = event.currentTarget;
    if (formTarget.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // API call to add eligible patient
    try {
      await eligiblesApi.eligiblesControllerCreate(form);
      setOffCanvasShow(false);
      reloadGrid && reloadGrid();
      showToaster({
        type: "success",
        title: "Eligible patient added successfully",
        icon: "check_circle_outline",
      });
    } catch (error:any) {
      showToaster({
        type: "danger",
        title: error.response.data.message.message.message ? error.response.data.message.message.message : "Failed to add eligible patient",
        icon: "error_outline",
      });
      console.error(error);
    }
  };

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = event.target;
    if (type === "tel") {
      // Remove all non-digit characters
      const numbersOnly = value.replace(/[^\d]/g, "");
      setForm({
        ...form,
        [name]: numbersOnly,
      });
      return;
    }

    if (name === "zipCode") {
      setInvalidZipCode(false);
      const zipcodeInput = document.querySelector(
        '[name="zipCode"]'
      ) as HTMLInputElement;
      zipcodeInput.setCustomValidity("");

      if (value.length === 5) {
        fetchZipcodeDetails(value);
      } else {
        setForm((prevForm) => ({
          ...prevForm,
          stateId: "",
          cityId: "",
        }));
        setSelectedCity("");
        setSelectedState("");
        setCities([]);
      }
    }

    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleDateChange = (name: string, event: MbscCalendarEvent) => {
    const { value } = event;
    const selectedDate = value ? new Date(value) : null;

    if (selectedDate) {
      const utcDate = new Date(
        Date.UTC(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        )
      );

      setForm({
        ...form,
        [name]: utcDate,
      });
    } else {
      setForm({
        ...form,
        [name]: null,
      });
    }

    if (event.value) {
      setError({
        ...error,
        [name]: false,
      });
    }
  };

  useEffect(() => {
    getContracts();
  }, []);

  return (
    <>
      {loading && <Spinner />}
      <Form
        className="d-flex flex-column h-100"
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
      >
        <SectionNavLayout>
            <Section title="Patient">
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  className="form-control"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  className="form-control"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-6">
                <label
                  htmlFor="dateOfBirth"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  DOB
                </label>
                <div className={`mobiscroll rounded-1 ${(validated && error.dateOfBirth) ? "invalid-input" : validated ? "valid-input" : ""}`}>
                  <div className="mobiscroll__calendar">
                    <Datepicker
                      id="dateOfBirth"
                      value={formatDateTask(form.dateOfBirth)}
                      controls={["calendar", "date"]}
                      calendarType="month"
                      theme="windows"
                      themeVariant="light"
                      placeholder="MM/DD/YYYY"
                      display="bubble"
                      max={maxDate18YearsAgo} // Set max date to 18 years ago
                      touchUi={true}
                      showOnClick={false}
                      isOpen={openPicker}
                      onClose={() => {
                        setOpenPicker(false);
                      }}
                      onChange={(e) => handleDateChange("dateOfBirth", e)}
                      buttons={[]}
                    />
                  </div>
                  <div
                    onClick={() => setOpenPicker(!openPicker)}
                    className="mobiscroll__calendar-icon"
                  >
                    <span className="material-symbols-outlined fs-18">
                      calendar_month
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">
                  Gender
                </label>
                <div className="d-flex align-items-center">
                  {genders?.map((gender) => (
                    <div className="form-check me-3" key={gender.id}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="genderId"
                        id={`genderId-${gender.id}`}
                        value={gender.id} // Using gender id as the value
                        checked={form.genderId === gender.id}
                        onChange={handleChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`genderId-${gender.id}`}
                      >
                        {gender.name} {/* Displaying the gender type */}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">Phone</label>
                <PhoneInput
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">Phone 2</label>
                <PhoneInput
                  name="mobilePhone"
                  value={form.mobilePhone}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">Email</label>
                <input
                  name="email"
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
            </Section>

            <Section title="Mailing Address">
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">Address Line</label>
                <input
                  type="text"
                  className="form-control lh-1"
                  placeholder="Address Line 1"
                  onChange={handleChange}
                  name="addressLine1"
                  value={form.addressLine1 || ''} />
              </div>
              <div className="col-12">
                <input
                  type="text"
                  className="form-control lh-1"
                  placeholder="Address Line 2"
                  onChange={handleChange}
                  name="addressLine2"
                  value={form.addressLine2 || ''} />
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">City</label>
                {cities.length === 0 ? (
                  <div className="form-control-plaintext">
                    {selectedCity ? selectedCity : <span className="text-light">City</span>}
                  </div>
                ) : (
                  <select
                    className="form-select"
                    name="cityId"
                    value={form.cityId || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.value}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">State</label>
                <div className="form-control-plaintext">
                  {selectedState ? selectedState : <span className="text-light">State</span>}
                </div>
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">Zipcode</label>
                <input
                  type="text"
                  className={`form-control lh-1 ${invalidZipCode && form.zipCode !== '' ? "border-red" : ""
                    }`}
                  placeholder="Zipcode"
                  onChange={handleChange}
                  maxLength={5}
                  name="zipCode"
                  value={form.zipCode || ''}
                />
                {invalidZipCode && form.zipCode !== '' ? (
                  <small style={{ color: "red" }}>Invalid Zipcode</small>
                ) : (
                  ""
                )}
              </div>
            </Section>

            <Section title="Partner">
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">
                  Contract
                </label>
                <select
                  className="form-select fs-12"
                  name="contractId"
                  id="contractId"
                  value={form.contractId}
                  onChange={handleChange}
                  required
                >
                  <option value="" >
                    Select Partner/Contract
                  </option>
                  {contracts!.length > 0 &&
                    contracts!.map((type: Contracts, index: number) => (
                      <option key={index} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">
                  External Patient ID from Partner
                </label>
                <input
                  type="text"
                  name="patientExternalIdFromPartner"
                  placeholder="External Patient ID from Partner"
                  className="form-control"
                  value={form.patientExternalIdFromPartner}
                  onChange={handleChange}
                />
              </div>
            </Section>

            <Section title="Care Team">
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">
                  PCP First Name
                </label>
                <input
                  type="text"
                  name="pcpFirstName"
                  placeholder="PCP First Name"
                  className="form-control"
                  value={form.pcpFirstName}
                  onChange={handleChange}
                />
              </div>
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">
                  PCP Last Name
                </label>
                <input
                  type="text"
                  name="pcpLastName"
                  placeholder="PCP Last Name"
                  className="form-control"
                  value={form.pcpLastName}
                  onChange={handleChange}
                />
              </div>
            </Section>
        </SectionNavLayout>
        <div className="offcanvas-footer w-100 border-top d-flex justify-content-between px-4 py-3">
          <div>
            <button
              className="btn btn-outline-secondary rounded-pill px-5 fs-13"
              type="reset"
              onClick={() => setOffCanvasShow(false)}
            >
              Discard
            </button>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-secondary rounded-pill px-5 fs-13"
              type="submit"
            >
              Add
            </button>
          </div>
        </div>
      </Form>
    </>
  );
};
